import { useState, useMemo } from "react";
import { calculateLMI, getLMIRate } from "../_data/lmiRates";

export interface LMIInputs {
  propertyValue: number;
  deposit: number;
  isFirstHomeBuyer: boolean;
  hasFamilyGuarantee: boolean;
}

export interface LMIResults {
  loanAmount: number;
  lvr: number;
  lmiRequired: boolean;
  lmiPremium: number;
  lmiRate: number;
  monthlyImpact: number; // If added to loan over 30 years
  totalWithLMI: number;
  depositNeededToAvoid: number;
  additionalDepositRequired: number;
}

const defaultInputs: LMIInputs = {
  propertyValue: 600000,
  deposit: 60000,
  isFirstHomeBuyer: false,
  hasFamilyGuarantee: false,
};

/**
 * LMI Calculator Hook
 */
export function useLMICalculator() {
  const [inputs, setInputs] = useState<LMIInputs>(defaultInputs);

  const results = useMemo<LMIResults>(() => {
    const { propertyValue, deposit, hasFamilyGuarantee } = inputs;

    const loanAmount = propertyValue - deposit;
    const lvr = (loanAmount / propertyValue) * 100;

    // No LMI with family guarantee or if LVR <= 80%
    const lmiRequired = lvr > 80 && !hasFamilyGuarantee;

    const lmiPremium = lmiRequired ? calculateLMI(loanAmount, propertyValue) : 0;
    const lmiRate = lmiRequired ? getLMIRate(loanAmount, propertyValue) : 0;

    // Calculate monthly impact if LMI added to loan (30 year term, 6% rate)
    const monthlyImpact = lmiPremium > 0 ? (lmiPremium * 0.06) / 12 + lmiPremium / 360 : 0;

    // Total loan with LMI capitalized
    const totalWithLMI = loanAmount + lmiPremium;

    // Deposit needed to avoid LMI (20% of property value)
    const depositNeededToAvoid = propertyValue * 0.2;
    const additionalDepositRequired = Math.max(0, depositNeededToAvoid - deposit);

    return {
      loanAmount: Math.round(loanAmount),
      lvr: Math.round(lvr * 100) / 100,
      lmiRequired,
      lmiPremium: Math.round(lmiPremium),
      lmiRate: Math.round(lmiRate * 100) / 100,
      monthlyImpact: Math.round(monthlyImpact),
      totalWithLMI: Math.round(totalWithLMI),
      depositNeededToAvoid: Math.round(depositNeededToAvoid),
      additionalDepositRequired: Math.round(additionalDepositRequired),
    };
  }, [inputs]);

  const updateInput = <K extends keyof LMIInputs>(key: K, value: LMIInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  // Helper to set deposit as percentage
  const setDepositPercent = (percent: number) => {
    updateInput("deposit", Math.round(inputs.propertyValue * (percent / 100)));
  };

  return { inputs, results, updateInput, setInputs, setDepositPercent };
}
